/**
 * notifications/NotificationBell.tsx — the header bell (NT-F09), mounted in App.tsx's frame between
 * GlobalSearch and AuthControl so it sits immediately left of the username, same position on every
 * page. Carries all five personal kinds the backend writes — see notifications/api.ts's header.
 *
 * Renders null when signed out (there is no feed to show); the caller decides sign-in state, this
 * component does not probe /api/me itself — App.tsx's own useAuth() result is passed in instead, to
 * avoid a second independent probe racing the frame's existing one.
 *
 * Polls every 60s (NT-NFR01's "within 15 min" latency budget has room to spare) and once on mount.
 * Opening the dropdown marks everything read (simplest "read" semantics for a bell) and revalidates.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { shortWhen } from '../community/shared';
import { BellIcon } from '../tools/upload/design/icons';
import { DropdownCard, DropdownDivider } from '../tools/upload/design/MenuBar';
import {
  getNotifications,
  markAllNotificationsRead,
  clearAllNotifications,
  type NotificationEntry,
} from './api';
// The kind -> line mapping now lives in its own pure module so the Kombi header can use the SAME one
// rather than becoming a third copy of it. Re-exported because test/notificationRows.test.ts imports
// `describe` from here, and because nothing else should have to care that it moved.
import { describe } from './describe';

export { describe };

const POLL_MS = 60_000;

export function NotificationBell({ signedIn }: { signedIn: boolean }) {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationEntry[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    const res = await getNotifications();
    if (res.kind === 'ok') {
      setUnreadCount(res.body.unreadCount);
      setNotifications(res.body.notifications);
    }
  }, []);

  useEffect(() => {
    if (!signedIn) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch-on-mount + poll, not derived state
    void refresh();
    const id = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(id);
  }, [signedIn, refresh]);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  if (!signedIn) return null;

  function toggleOpen() {
    const next = !open;
    setOpen(next);
    if (next && unreadCount > 0) {
      setUnreadCount(0);
      void markAllNotificationsRead().then(() => void refresh());
    }
  }

  return (
    <div className="tb-notif" ref={containerRef}>
      {/* The Menu Bar's designed bell: the frame's bell glyph in a borderless 32×32 icon
          button (no outline, per the product owner — it lives inside the profile cell now,
          not as a standalone outlined control in the account cluster). The unread dot is an
          absolutely-positioned sibling — `.tb-notif` is position:relative — so it overlays
          the button's top-right corner. The panel below stays V1 `.tb-notif__*` chrome: a
          dropdown is a dropdown regardless of the trigger's skin. */}
      <button
        type="button"
        className="flex size-[32px] items-center justify-center rounded-md bg-transparent text-icon transition-colors hover:bg-accent-subtle focus-visible:border focus-visible:border-ring-focus focus-visible:outline-none"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        onClick={toggleOpen}
      >
        {/* BellIcon at the full 32 so its viewBox maps 1:1 onto the button and the glyph
            lands at exactly the frame's coordinates (its native box is upper-left of centre,
            so a smaller centred icon would sit off-centre — see BellIcon's header). */}
        <BellIcon size={32} />
      </button>
      {unreadCount > 0 ? <span className="tb-notif__dot" aria-hidden="true" /> : null}

      {/* The panel now wears the design-system dropdown skin (DropdownCard + DropdownRow-style
          rows), matching the profile menu — a dropdown is a dropdown regardless of trigger. The
          card is widened past the 243px default because these rows carry a title AND a timestamp,
          not a single short label. Unread rows are marked with the same accent dot as the bell,
          inline before the text (font-weight stays uniform — the dot is the cue, per NT-F06/F07). */}
      {open ? (
        <DropdownCard className="w-[280px]">
          {notifications.length === 0 ? (
            <p className="px-5 py-2 text-xs font-light text-icon">No notifications yet.</p>
          ) : (
            <>
              {notifications.map((n) => {
                // EVERY ROW DRAWS. `describe()` returns `{text, href}` unconditionally — it has no
                // null arm, and its fallbacks ('/me', '/community') exist precisely so an unknown kind
                // or a malformed payload still yields a row rather than nothing. This map used to carry
                // an `if (!d) return null` guard; it was dead against that signature, and silently
                // dropping a row would ALSO desync the panel from the unread count, which is computed
                // from `notifications` and not from what rendered.
                const d = describe(n);
                const unread = n.readAt === null;
                return (
                  <button
                    key={n.notificationId}
                    type="button"
                    role="menuitem"
                    className="flex w-full flex-col items-start gap-0.5 rounded-md px-5 py-2 text-left text-xs font-light text-ink transition-colors hover:bg-accent-subtle focus-visible:bg-accent-subtle focus-visible:outline-none"
                    onClick={() => {
                      setOpen(false);
                      navigate(d.href);
                    }}
                  >
                    <span className="flex items-center gap-2">
                      {unread ? (
                        <span
                          aria-hidden="true"
                          className="size-[6px] shrink-0 rounded-full bg-accent"
                        />
                      ) : null}
                      {d.text}
                    </span>
                    <span className="text-[10px] text-icon">{shortWhen(n.createdAt)}</span>
                  </button>
                );
              })}

              <DropdownDivider />

              <button
                type="button"
                className="w-full rounded-md px-5 py-2 text-center text-xs font-light text-icon transition-colors hover:bg-accent-subtle focus-visible:bg-accent-subtle focus-visible:outline-none"
                onClick={() => {
                  void clearAllNotifications().then(() => void refresh());
                }}
              >
                Clear all
              </button>
            </>
          )}
        </DropdownCard>
      ) : null}
    </div>
  );
}
